import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Download, Printer, Send, FileCheck2, Paperclip, ScanLine } from "lucide-react";
import { downloadCotizacionPdf, printCotizacionPdf, downloadSinglePagePdf } from "@/lib/pdf";
import api from "@/lib/api";

const LOGO = "/logo-cooper.png";
const ESTADOS_MX = [
  "Aguascalientes", "Baja California", "Baja California Sur", "Campeche", "Chiapas", "Chihuahua",
  "Ciudad de México", "Coahuila", "Colima", "Durango", "Estado de México", "Guanajuato", "Guerrero",
  "Hidalgo", "Jalisco", "Michoacán", "Morelos", "Nayarit", "Nuevo León", "Oaxaca", "Puebla",
  "Querétaro", "Quintana Roo", "San Luis Potosí", "Sinaloa", "Sonora", "Tabasco", "Tamaulipas",
  "Tlaxcala", "Veracruz", "Yucatán", "Zacatecas",
];
const TIPO_LABEL = { contado: "Contado", credito: "Crédito", contado_credito: "Contado / Crédito" };

const EMPTY = {
  nombre_razon_social: "", nombre_contacto: "", rfc: "", email: "", telefono: "", whatsapp: "",
  calle: "", no_exterior: "", no_interior: "", colonia: "", codigo_postal: "", municipio_alcaldia: "", estado: "",
  entrega_nombre_recibe: "", entrega_telefono: "", entrega_calle_numero: "", entrega_colonia: "",
  entrega_municipio: "", entrega_estado: "", entrega_ubicacion_maps: "", observaciones: "",
  tipo_operacion: "contado", tipo_persona: "fisica", limite_credito: "", dias_credito: "",
};
const blankForm = () => ({ ...EMPTY });

const DOC_DEFS = [
  { key: "csf", tipo: "CSF", label: "Constancia de Situación Fiscal (CSF)" },
  { key: "domicilio", tipo: "Comprobante de domicilio", label: "Comprobante de domicilio" },
  { key: "ine", tipo: "INE del titular / representante", label: "INE del titular / representante" },
  { key: "acta", tipo: "Acta constitutiva", label: "Acta constitutiva" },
  { key: "poder", tipo: "Poder del representante legal", label: "Poder del representante legal" },
  { key: "ine_aval", tipo: "INE del aval", label: "INE del aval" },
  { key: "comprobante_aval", tipo: "Comprobante de domicilio del aval", label: "Comprobante de domicilio del aval" },
];
const ENTREGA_REQ = ["entrega_nombre_recibe", "entrega_telefono", "entrega_calle_numero", "entrega_colonia", "entrega_municipio", "entrega_estado"];

const CONECTORES = new Set([
  "de", "del", "la", "las", "los", "el", "y", "e", "o", "u", "a", "en", "con", "por", "para", "al", "da", "do",
]);
const titleCase = (str) => {
  if (!str) return str;
  let primera = true;
  return str.toLowerCase().replace(/[^\s]+/g, (w) => {
    const esPrimera = primera;
    primera = false;
    if (!esPrimera && CONECTORES.has(w)) return w;
    return w.charAt(0).toUpperCase() + w.slice(1);
  });
};

const Row = ({ label, value }) => (
  <div className="flex text-xs border-b border-gray-200 py-1">
    <span className="w-44 shrink-0 font-semibold text-gray-700">{label}</span>
    <span className="flex-1">{value || "—"}</span>
  </div>
);

const Field = ({ label, value, onChange, onBlur, ph, className, testid }) => (
  <div className={`space-y-1 ${className || ""}`}>
    <Label className="text-xs">{label}</Label>
    <Input value={value} onChange={onChange} onBlur={onBlur} placeholder={ph} data-testid={testid} />
  </div>
);

const m = (v) => "$" + Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function SolicitudAltaCliente() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [f, setF] = useState(blankForm);
  const [docs, setDocs] = useState({});
  const [mismoDomicilio, setMismoDomicilio] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const cartaAvalRef = useRef(null);
  const cartaRefRef = useRef(null);

  const esCredito = f.tipo_operacion === "credito" || f.tipo_operacion === "contado_credito";
  const moral = f.tipo_persona === "moral";

  const docVisible = (k) => {
    if (k === "acta" || k === "poder") return esCredito && moral;
    if (k === "ine_aval" || k === "comprobante_aval") return esCredito;
    return true;
  };
  const docRequired = (k) => {
    if (k === "csf" || k === "domicilio") return true;
    if (k === "ine") return esCredito;
    if (k === "acta" || k === "poder") return esCredito && moral;
    if (k === "ine_aval" || k === "comprobante_aval") return esCredito;
    return false;
  };

  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e?.target ? e.target.value : e }));
  const applyTransform = (k, type) => () =>
    setF((prev) => ({ ...prev, [k]: type === "upper" ? (prev[k] || "").toUpperCase() : titleCase(prev[k] || "") }));

  const field = (k, label, { className, transform } = {}) =>
    Field({ label, value: f[k], onChange: set(k), onBlur: transform ? applyTransform(k, transform) : undefined, className, testid: `f-${k}` });

  const validFile = (file) => {
    if (!file) return false;
    if (!["application/pdf", "image/jpeg", "image/png"].includes(file.type)) { toast.error("Solo se permiten archivos PDF, JPG o PNG."); return false; }
    if (file.size > 15 * 1024 * 1024) { toast.error("El archivo supera el límite de 15 MB."); return false; }
    return true;
  };
  const setDoc = (key) => (e) => {
    const file = e.target.files?.[0];
    if (!file) { setDocs((p) => ({ ...p, [key]: undefined })); return; }
    if (!validFile(file)) { e.target.value = ""; return; }
    setDocs((p) => ({ ...p, [key]: file }));
  };

  const toggleMismoDomicilio = (checked) => {
    setMismoDomicilio(checked);
    if (checked) {
      const calleNumero = [f.calle, f.no_exterior && `#${f.no_exterior}`, f.no_interior && `Int. ${f.no_interior}`].filter(Boolean).join(" ");
      setF((prev) => ({ ...prev, entrega_calle_numero: calleNumero, entrega_colonia: prev.colonia, entrega_municipio: prev.municipio_alcaldia, entrega_estado: prev.estado }));
    }
  };

  const fecha = new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
  const vendedor = user?.full_name || user?.username || "";
  const hayEntrega = ["entrega_nombre_recibe", "entrega_telefono", "entrega_calle_numero", "entrega_colonia", "entrega_municipio", "entrega_estado", "entrega_ubicacion_maps"].some((k) => f[k]);

  const validarBase = () => {
    if (!f.nombre_razon_social || !f.nombre_contacto || !f.rfc || !f.telefono) {
      toast.error("Completa al menos: Razón social, Contacto, RFC y Teléfono");
      return false;
    }
    return true;
  };
  const validarEnvio = () => {
    if (!validarBase()) return false;
    if (ENTREGA_REQ.some((k) => !f[k])) {
      toast.error("Completa los datos de entrega (quién recibe, teléfono y dirección). Puedes usar la casilla de mismos datos del domicilio fiscal.");
      return false;
    }
    if (esCredito && (!Number(f.limite_credito) || !Number(f.dias_credito))) {
      toast.error("Para crédito, indica el límite y los días de crédito.");
      return false;
    }
    if (!user?.permite_sin_documentos) {
      const faltan = DOC_DEFS.filter((d) => docRequired(d.key) && !docs[d.key]).map((d) => d.tipo);
      if (faltan.length) { toast.error(`Faltan documentos obligatorios: ${faltan.join(", ")}`); return false; }
    }
    return true;
  };

  const handleDownload = () => { if (validarBase()) downloadCotizacionPdf(bodyRef.current, headerRef.current, `Solicitud de alta - ${f.nombre_razon_social}`); };
  const handlePrint = () => { if (validarBase()) printCotizacionPdf(bodyRef.current, headerRef.current); };

  const descargarCartaAval = () => {
    if (!f.nombre_razon_social) { toast.error("Captura el nombre del cliente antes de descargar el formato."); return; }
    downloadSinglePagePdf(cartaAvalRef.current, `Carta de aval - ${f.nombre_razon_social}`);
  };
  const descargarCartaRef = () => {
    if (!f.nombre_razon_social) { toast.error("Captura el nombre del cliente antes de descargar el formato."); return; }
    downloadSinglePagePdf(cartaRefRef.current, `Carta de referencias - ${f.nombre_razon_social}`);
  };
  const FORMATOS = [
    { key: "contrato", label: "Contrato de crédito", enabled: false },
    { key: "pagare", label: "Pagaré", enabled: false },
    { key: "carta_aval", label: "Carta de aval", enabled: true, onClick: descargarCartaAval },
    { key: "carta_ref", label: "Carta de referencias comerciales", enabled: true, onClick: descargarCartaRef },
  ];

  const handleEnviar = async () => {
    if (!validarEnvio()) return;
    setEnviando(true);
    try {
      const payload = {
        ...f,
        tipo_operacion: f.tipo_operacion,
        tipo_persona: f.tipo_persona,
        solicita_credito: esCredito,
        limite_credito: esCredito && f.limite_credito ? Number(f.limite_credito) : null,
        dias_credito: esCredito && f.dias_credito ? parseInt(f.dias_credito, 10) : null,
        aval_nombre: null,
        referencias: null,
      };
      const { data: sol } = await api.post("/solicitudes", payload);
      for (const d of DOC_DEFS) {
        const file = docs[d.key];
        if (!file) continue;
        const fd = new FormData();
        fd.append("entidad_tipo", "solicitud");
        fd.append("entidad_id", sol.id);
        fd.append("tipo_documento", d.tipo);
        fd.append("file", file);
        await api.post("/documentos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Solicitud enviada a administración con sus documentos. Te avisaremos cuando sea aprobada.");
      setF(blankForm()); setDocs({}); setMismoDomicilio(false);
      document.querySelectorAll('input[type="file"]').forEach((i) => (i.value = ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar la solicitud");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="alta-cliente-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/data")}><ArrowLeft className="h-4 w-4 mr-2" /> DATA</Button>
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Solicitud de alta de cliente</h1>
          <p className="text-muted-foreground mt-2">Llena los datos, adjunta los documentos y envíalos a administración para su aprobación.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleEnviar} disabled={enviando} data-testid="alta-enviar"><Send className="h-4 w-4 mr-2" /> {enviando ? "Enviando…" : "Enviar a administración"}</Button>
          <Button variant="outline" size="sm" onClick={handleDownload} data-testid="alta-descargar"><Download className="h-4 w-4 mr-2" /> Descargar PDF</Button>
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="alta-imprimir"><Printer className="h-4 w-4 mr-2" /> Imprimir</Button>
        </div>
      </div>

      {/* Datos generales */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Datos generales</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {field("nombre_razon_social", "Nombre / Razón social *", { className: "sm:col-span-2", transform: "title" })}
          {field("nombre_contacto", "Nombre de contacto *", { transform: "title" })}
          {field("rfc", "RFC *", { transform: "upper" })}
          {field("email", "Correo electrónico")}
          {field("telefono", "Teléfono *")}
          {field("whatsapp", "WhatsApp")}
        </CardContent>
      </Card>

      {/* Dirección fiscal */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Dirección fiscal</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {field("calle", "Calle", { className: "sm:col-span-2", transform: "title" })}
          {field("no_exterior", "No. exterior")}
          {field("no_interior", "No. interior")}
          {field("colonia", "Colonia", { transform: "title" })}
          {field("codigo_postal", "Código postal")}
          {field("municipio_alcaldia", "Municipio / Alcaldía", { transform: "title" })}
          <div className="space-y-1">
            <Label className="text-xs">Estado</Label>
            <Select value={f.estado} onValueChange={set("estado")}>
              <SelectTrigger data-testid="f-estado"><SelectValue placeholder="Selecciona estado" /></SelectTrigger>
              <SelectContent>{ESTADOS_MX.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Datos de entrega (obligatorios) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Datos de entrega</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">Necesarios para poder surtir la mercancía.</p>
          <label className="flex items-center gap-2 mt-2 cursor-pointer select-none text-sm" data-testid="mismo-domicilio-label">
            <input type="checkbox" checked={mismoDomicilio} onChange={(e) => toggleMismoDomicilio(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 accent-black cursor-pointer" data-testid="mismo-domicilio-checkbox" />
            Utilizar mismos datos del domicilio fiscal
          </label>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {field("entrega_nombre_recibe", "Quién recibe *", { transform: "title" })}
          {field("entrega_telefono", "Teléfono de entrega *")}
          {field("entrega_calle_numero", "Calle y número *", { className: "sm:col-span-2", transform: "title" })}
          {field("entrega_colonia", "Colonia *", { transform: "title" })}
          {field("entrega_municipio", "Municipio *", { transform: "title" })}
          <div className="space-y-1">
            <Label className="text-xs">Estado de entrega *</Label>
            <Select value={f.entrega_estado} onValueChange={set("entrega_estado")}>
              <SelectTrigger data-testid="f-entrega_estado"><SelectValue placeholder="Selecciona estado" /></SelectTrigger>
              <SelectContent>{ESTADOS_MX.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {field("entrega_ubicacion_maps", "Ubicación (link de Maps)")}
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">Observaciones</Label>
            <textarea value={f.observaciones} onChange={set("observaciones")} onBlur={applyTransform("observaciones", "title")} data-testid="f-observaciones" rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Comentarios adicionales para administración" />
          </div>
        </CardContent>
      </Card>

      {/* Condiciones comerciales */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Condiciones comerciales</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Tipo de persona *</Label>
            <Select value={f.tipo_persona} onValueChange={set("tipo_persona")}>
              <SelectTrigger data-testid="f-tipo_persona"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fisica">Persona física</SelectItem>
                <SelectItem value="moral">Persona moral (empresa)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Tipo de operación *</Label>
            <Select value={f.tipo_operacion} onValueChange={set("tipo_operacion")}>
              <SelectTrigger data-testid="f-tipo_operacion"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="contado">Contado</SelectItem>
                <SelectItem value="credito">Crédito</SelectItem>
                <SelectItem value="contado_credito">Contado / Crédito</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {esCredito && (
            <>
              <div className="space-y-1">
                <Label className="text-xs">Límite de crédito (MXN) *</Label>
                <Input type="number" min="0" value={f.limite_credito} onChange={set("limite_credito")} placeholder="Ej. 50000" data-testid="f-limite_credito" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Días de crédito *</Label>
                <Input type="number" min="0" value={f.dias_credito} onChange={set("dias_credito")} placeholder="Ej. 30" data-testid="f-dias_credito" />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Formatos para firmar (solo crédito) */}
      {esCredito && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Formatos para firmar</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Descarga el formato pre-llenado, imprímelo, recábalo firmado por el cliente/aval, escanéalo y súbelo (el Contrato firmado y el Pagaré se cargan en la ficha del cliente al activar el crédito).</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {FORMATOS.map((ft) => (
              <div key={ft.key} className="flex items-center justify-between border rounded-md p-3" data-testid={`formato-row-${ft.key}`}>
                <span className="text-sm font-medium">{ft.label}</span>
                {ft.enabled ? (
                  <Button variant="outline" size="sm" onClick={ft.onClick} data-testid={`formato-${ft.key}`}><Download className="h-4 w-4 mr-2" /> Llenar y descargar</Button>
                ) : (
                  <span className="text-xs text-muted-foreground px-2 py-1 rounded bg-muted border" data-testid={`formato-proximamente-${ft.key}`}>Próximamente</span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Documentos */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Paperclip className="h-5 w-5" /> Documentos</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {esCredito
              ? (moral ? "Crédito · Persona moral: CSF, comprobante, INE, acta constitutiva, poder, e INE + comprobante del aval." : "Crédito · Persona física: CSF, comprobante, INE, e INE + comprobante del aval.")
              : "Contado: CSF y comprobante de domicilio (el INE es opcional)."} El contrato y pagaré se suben al activar el crédito. PDF, JPG o PNG · máx. 15 MB c/u.
          </p>
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="doc-aviso-escaneo">
            <p className="font-semibold flex items-center gap-2"><ScanLine className="h-4 w-4" /> El documento debe estar escaneado o ser una foto nítida, recta y completa (sin recortes ni reflejos).</p>
            <p className="mt-2 font-medium">¿Cómo escanear con el celular?</p>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li><span className="font-medium">iPhone:</span> app Notas o Archivos → "Escanear documentos" (recorta y endereza solo, exporta PDF).</li>
              <li><span className="font-medium">Android:</span> Google Drive → botón "+" → "Escanear"; o Google Fotos/Files.</li>
              <li><span className="font-medium">Apps gratis:</span> Adobe Scan, Microsoft Lens o CamScanner.</li>
            </ul>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {DOC_DEFS.filter((d) => docVisible(d.key)).map((d) => {
            const requerido = docRequired(d.key);
            return (
              <div key={d.key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 border rounded-md p-3">
                <div className="flex items-center gap-2 sm:w-80 shrink-0">
                  <span className="text-sm font-medium">{d.label} {requerido ? <span className="text-red-500">*</span> : <span className="text-muted-foreground">(opcional)</span>}</span>
                </div>
                <Input type="file" accept=".pdf,image/jpeg,image/png" onChange={setDoc(d.key)} className="flex-1" data-testid={`doc-${d.key}`} />
                {docs[d.key] && <span className="text-xs text-green-600 inline-flex items-center gap-1"><FileCheck2 className="h-4 w-4" /> {docs[d.key].name}</span>}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Formatos membretados para firmar (se descargan como PDF) */}
      <div style={{ position: "absolute", left: "-10000px", top: 0 }} aria-hidden>
        <div ref={cartaAvalRef} style={{ width: "750px", background: "#fff", padding: "32px", color: "#111", fontFamily: "Arial, sans-serif", fontSize: "13px", lineHeight: 1.7 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", borderBottom: "3px solid #000", paddingBottom: "10px" }}>
            <img src={LOGO} style={{ height: "56px", width: "56px", objectFit: "contain" }} alt="" />
            <div><div style={{ fontSize: "16px", fontWeight: 800 }}>DUELAS COOPER SLIP</div><div style={{ fontSize: "12px" }}>Carta de Aval / Obligado Solidario</div></div>
          </div>
          <p style={{ textAlign: "right", marginTop: "12px" }}>Fecha: {fecha}</p>
          <h2 style={{ textAlign: "center", fontSize: "15px", fontWeight: 800, margin: "12px 0" }}>CARTA DE AVAL Y OBLIGADO SOLIDARIO</h2>
          <p>Por medio del presente documento, el/la que suscribe <b>{f.aval_nombre || "________________________"}</b>, con domicilio en ______________________________________________________, me constituyo de manera libre y voluntaria como <b>AVAL y OBLIGADO SOLIDARIO</b> del cliente <b>{f.nombre_razon_social || "________________"}</b> (RFC {f.rfc || "____________"}) ante <b>Duelas Cooper Slip</b>.</p>
          <p>En tal carácter, me obligo solidariamente a responder por la línea de crédito otorgada por un monto de hasta <b>{f.limite_credito ? m(f.limite_credito) : "$______________"}</b>, con un plazo de <b>{f.dias_credito || "____"} días</b>, así como por sus accesorios legales, en caso de que el cliente no cumpla con sus obligaciones de pago, renunciando expresamente a los beneficios de orden y excusión.</p>
          <div style={{ marginTop: "70px", display: "flex", justifyContent: "center" }}>
            <div style={{ borderTop: "1px solid #666", width: "60%", paddingTop: "6px", textAlign: "center" }}>Nombre y firma del aval</div>
          </div>
          <p style={{ marginTop: "24px", fontSize: "10px", color: "#777", textAlign: "center" }}>Duelas Cooper Slip — documento de expediente de crédito.</p>
        </div>

        <div ref={cartaRefRef} style={{ width: "750px", background: "#fff", padding: "32px", color: "#111", fontFamily: "Arial, sans-serif", fontSize: "13px", lineHeight: 1.7 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", borderBottom: "3px solid #000", paddingBottom: "10px" }}>
            <img src={LOGO} style={{ height: "56px", width: "56px", objectFit: "contain" }} alt="" />
            <div><div style={{ fontSize: "16px", fontWeight: 800 }}>DUELAS COOPER SLIP</div><div style={{ fontSize: "12px" }}>Carta de Referencias Comerciales</div></div>
          </div>
          <p style={{ textAlign: "right", marginTop: "12px" }}>Fecha: {fecha}</p>
          <h2 style={{ textAlign: "center", fontSize: "15px", fontWeight: 800, margin: "12px 0" }}>CARTA DE REFERENCIAS COMERCIALES</h2>
          <p>El cliente <b>{f.nombre_razon_social || "________________"}</b> (RFC {f.rfc || "____________"}) proporciona las siguientes referencias comerciales y autoriza a <b>Duelas Cooper Slip</b> a contactarlas para verificar su historial de pagos:</p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", marginTop: "10px" }}>
            <thead><tr><th style={{ textAlign: "left", border: "1px solid #999", padding: "6px" }}>#</th><th style={{ textAlign: "left", border: "1px solid #999", padding: "6px" }}>Proveedor</th><th style={{ textAlign: "left", border: "1px solid #999", padding: "6px" }}>Teléfono</th></tr></thead>
            <tbody>
              {[0, 1, 2, 3, 4].map((i) => (
                <tr key={i}><td style={{ border: "1px solid #ccc", padding: "10px 6px" }}>{i + 1}</td><td style={{ border: "1px solid #ccc", padding: "10px 6px" }}></td><td style={{ border: "1px solid #ccc", padding: "10px 6px" }}></td></tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: "70px", display: "flex", justifyContent: "center" }}>
            <div style={{ borderTop: "1px solid #666", width: "60%", paddingTop: "6px", textAlign: "center" }}>Nombre y firma del cliente</div>
          </div>
          <p style={{ marginTop: "24px", fontSize: "10px", color: "#777", textAlign: "center" }}>Duelas Cooper Slip — documento de expediente de crédito.</p>
        </div>
      </div>

      {/* Vista previa membretada (lo que se exporta) */}
      <div className="bg-gray-200 rounded-md p-4 sm:p-8 overflow-x-auto flex justify-center" data-testid="alta-preview">
        <div className="bg-white shadow" style={{ width: "816px", minHeight: "1056px" }}>
          <div ref={headerRef}>
            <div className="px-8 pt-6">
              <div className="grid grid-cols-[auto_1fr_auto] gap-6 items-start bg-gray-50 px-3 py-3">
                <div className="text-center">
                  <img src={LOGO} className="h-16 w-16 object-contain rounded-lg mx-auto" alt="Cooper Slip" />
                  <div className="text-xs mt-1">Cooper Slip</div>
                </div>
                <div className="text-center text-sm font-bold leading-snug pt-3">
                  <p>Cuando tu cliente queda bien, Tu quedas mejor.</p>
                  <p>Elije las duelas que respaldan a tu negocio</p>
                </div>
                <div className="text-right">
                  <h2 className="text-xl font-extrabold tracking-tight leading-tight">SOLICITUD DE<br />ALTA DE CLIENTE</h2>
                  <p className="text-xs mt-1"><span className="font-bold">Fecha:</span> {fecha}</p>
                  <p className="text-xs"><span className="font-bold">Vendedor:</span> {vendedor}</p>
                </div>
              </div>
              <div className="border-t-4 border-black" />
            </div>
          </div>

          <div ref={bodyRef} className="px-8 py-6 text-gray-900">
            <h3 className="text-sm font-bold mb-1 bg-gray-100 px-2 py-1">Datos generales</h3>
            <Row label="Nombre / Razón social" value={f.nombre_razon_social} />
            <Row label="Nombre de contacto" value={f.nombre_contacto} />
            <Row label="RFC" value={f.rfc} />
            <Row label="Correo electrónico" value={f.email} />
            <Row label="Teléfono" value={f.telefono} />
            <Row label="WhatsApp" value={f.whatsapp} />

            <h3 className="text-sm font-bold mb-1 mt-4 bg-gray-100 px-2 py-1">Dirección fiscal</h3>
            <Row label="Calle" value={f.calle} />
            <Row label="No. exterior / interior" value={[f.no_exterior, f.no_interior].filter(Boolean).join(" / ")} />
            <Row label="Colonia" value={f.colonia} />
            <Row label="Código postal" value={f.codigo_postal} />
            <Row label="Municipio / Alcaldía" value={f.municipio_alcaldia} />
            <Row label="Estado" value={f.estado} />

            <h3 className="text-sm font-bold mb-1 mt-4 bg-gray-100 px-2 py-1">Condiciones comerciales</h3>
            <Row label="Tipo de persona" value={moral ? "Persona moral" : "Persona física"} />
            <Row label="Tipo de operación" value={TIPO_LABEL[f.tipo_operacion]} />
            {esCredito && (
              <>
                <Row label="Límite de crédito" value={f.limite_credito ? m(f.limite_credito) : ""} />
                <Row label="Días de crédito" value={f.dias_credito ? `${f.dias_credito} días` : ""} />
              </>
            )}

            {hayEntrega && (
              <>
                <h3 className="text-sm font-bold mb-1 mt-4 bg-gray-100 px-2 py-1">Datos de entrega</h3>
                <Row label="Quién recibe" value={f.entrega_nombre_recibe} />
                <Row label="Teléfono de entrega" value={f.entrega_telefono} />
                <Row label="Calle y número" value={f.entrega_calle_numero} />
                <Row label="Colonia" value={f.entrega_colonia} />
                <Row label="Municipio" value={f.entrega_municipio} />
                <Row label="Estado" value={f.entrega_estado} />
                <Row label="Ubicación (Maps)" value={f.entrega_ubicacion_maps} />
              </>
            )}

            {f.observaciones && (
              <>
                <h3 className="text-sm font-bold mb-1 mt-4 bg-gray-100 px-2 py-1">Observaciones</h3>
                <p className="text-xs whitespace-pre-wrap">{f.observaciones}</p>
              </>
            )}

            <div className="mt-10 text-xs" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <div className="mt-8 border-t border-gray-400 w-64 pt-1">Firma / nombre del vendedor</div>
              <p className="text-gray-600 mt-1">Duelas Cooper Slip — Solicitud de alta de cliente</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
